/**
 * The format catalog — the machine-readable form of the §6 technical
 * feasibility matrix.
 *
 * `docs/02-format-feasibility-matrix.md` is generated from this file by
 * `tools/generate-format-matrix.mjs`, and the runtime file-information screen
 * (§31) reads the same records. Documentation and product behaviour cannot
 * drift apart, which is how §19 ("never label incomplete support as full
 * support") is enforced structurally rather than by review discipline.
 *
 * Library versions and licenses below were verified against the npm registry
 * on 2026-07-24; see `docs/03-license-inventory.md`.
 */
import type {
  FidelityProfile, FormatSpec, PlatformSupport, SupportLevel,
} from './support.js';

/* ------------------------------------------------------------------ *
 * helpers
 * ------------------------------------------------------------------ */

/** Fidelity profile in fixed order: layout, fonts, tables, images, charts, formulas. */
const fid = (
  layout: FidelityProfile['layout'], fonts: FidelityProfile['fonts'],
  tables: FidelityProfile['tables'], images: FidelityProfile['images'],
  charts: FidelityProfile['charts'], formulas: FidelityProfile['formulas'],
): FidelityProfile => ({ layout, fonts, tables, images, charts, formulas });

/** Same support on every platform — true for anything the TS core handles alone. */
const uniform = (view: SupportLevel, edit: SupportLevel): PlatformSupport => ({
  android: [view, edit], ios: [view, edit],
  windows: [view, edit], macos: [view, edit], web: [view, edit],
});

/** Platforms differ — used where an OS decoder is involved (HEIC, RAW, video). */
const perPlatform = (
  m: Partial<Record<keyof PlatformSupport, readonly [SupportLevel, SupportLevel]>>,
  fallback: readonly [SupportLevel, SupportLevel],
): PlatformSupport => ({
  android: m.android ?? fallback, ios: m.ios ?? fallback,
  windows: m.windows ?? fallback, macos: m.macos ?? fallback, web: m.web ?? fallback,
});

const LIB = {
  pdfjs: { name: 'pdfjs-dist', version: '6.1.200', license: 'Apache-2.0', role: 'PDF 렌더링·텍스트 레이어·검색' },
  pdflib: { name: 'pdf-lib', version: '1.17.1', license: 'MIT', role: 'PDF 주석·페이지 조작·폼·암호화·저장' },
  sheetjs: { name: 'xlsx', version: '0.18.5', license: 'Apache-2.0', role: '넓은 스프레드시트 포맷 읽기' },
  exceljs: { name: 'exceljs', version: '4.4.0', license: 'MIT', role: 'XLSX 스타일·수식 읽기/쓰기' },
  mammoth: { name: 'mammoth', version: '1.12.0', license: 'BSD-2-Clause', role: 'DOCX → 시맨틱 HTML 변환' },
  docx: { name: 'docx', version: '9.7.1', license: 'MIT', role: 'DOCX 생성' },
  pptxgen: { name: 'pptxgenjs', version: '4.0.1', license: 'MIT', role: 'PPTX 생성' },
  jszip: { name: 'jszip', version: '3.10.1', license: 'MIT (듀얼 중 MIT 선택)', role: 'OOXML/OWPML/EPUB ZIP 컨테이너 입출력' },
  fflate: { name: 'fflate', version: '0.8.3', license: 'MIT', role: '스트리밍 ZIP·gzip·중앙디렉터리 전용 읽기' },
  fxp: { name: 'fast-xml-parser', version: '5.10.1', license: 'MIT', role: 'OOXML/OWPML/SVG/XML 파싱·직렬화' },
  papaparse: { name: 'papaparse', version: '5.5.4', license: 'MIT', role: 'CSV/TSV 스트리밍 파싱' },
  sqljs: { name: 'sql.js', version: '1.14.1', license: 'MIT', role: 'SQLite 읽기 (WASM)' },
  epubjs: { name: 'epubjs', version: '0.3.93', license: 'BSD-2-Clause', role: 'EPUB 렌더링' },
  three: { name: 'three', version: '0.185.1', license: 'MIT', role: '3D 미리보기' },
  agpsd: { name: 'ag-psd', version: '31.0.2', license: 'MIT', role: 'PSD 레이어·합성 이미지 읽기' },
  dxfparser: { name: 'dxf-parser', version: '1.1.2', license: 'MIT', role: 'DXF 엔티티 파싱' },
  filetype: { name: 'file-type', version: '22.0.1', license: 'MIT', role: '매직바이트 판별 보조' },
  dompurify: { name: 'dompurify', version: '3.4.12', license: 'MPL-2.0 (듀얼 중 선택)', role: 'HTML/SVG 스크립트 제거 (§30)' },
  prism: { name: 'prismjs', version: '1.30.0', license: 'MIT', role: '소스코드 문법 강조' },
  marked: { name: 'marked', version: '18.0.7', license: 'MIT', role: 'Markdown 파싱' },
  wavesurfer: { name: 'wavesurfer.js', version: '7.12.11', license: 'BSD-3-Clause', role: '오디오 파형 렌더링' },
  osImage: { name: 'OS image decoder', version: '—', license: 'OS 제공', role: 'Android ImageDecoder / iOS CIImage / Windows WIC' },
  osMedia: { name: 'OS media stack', version: '—', license: 'OS 제공', role: '플랫폼 코덱으로 재생 (§20 특허 회피)' },
  browser: { name: 'Browser native', version: '—', license: '—', role: '브라우저 내장 디코더/플레이어' },
} as const;

/** Threats shared by every ZIP-based Office/Hangul container. */
const OOXML_RISKS = [
  '매크로(VBA) 포함 가능 — 실행 절대 금지 (§30)',
  '외부 데이터 연결·원격 이미지·OLE 임베드 — 네트워크 요청 차단 (§30)',
  'ZIP 기반이므로 zip bomb / 과다 엔트리 위험 (§21)',
  'XXE(외부 엔티티) — XML 파서에서 엔티티 확장 비활성화 필수',
];

/** Round-trip guarantee for our part-preserving OOXML/OWPML pipeline. */
const PART_PRESERVING = '편집한 XML 파트만 교체하고 나머지 원본 파트는 바이트 단위로 보존한다. 미지원 기능이 구조적으로 유지된다 (§13, §24).';

/* ------------------------------------------------------------------ *
 * §7.1 Documents
 * ------------------------------------------------------------------ */

const documents: FormatSpec[] = [
  {
    id: 'pdf', label: 'PDF', extensions: ['.pdf'], mimeTypes: ['application/pdf'],
    family: 'document', spec: 'ISO 32000-2:2020 (PDF 2.0)', phase: 1,
    view: 'full-view', edit: 'annotate-edit', platforms: uniform('full-view', 'annotate-edit'),
    processing: 'local', fidelity: fid('high', 'high', 'high', 'high', 'high', 'n/a'),
    passwordSupport: 'handled', encryptionSupport: 'handled',
    largeFileRisk: 'medium', softSizeLimit: '모바일 200 MB / 데스크톱 2 GB',
    lossOnSave: [
      'pdf-lib 재저장 시 일부 비표준 생성기의 증분 갱신 구조가 정규화될 수 있음',
      'XFA 동적 폼은 보존만 하고 편집 불가',
    ],
    libraries: [LIB.pdfjs, LIB.pdflib],
    securityRisks: [
      'JavaScript 액션 임베드 — pdf.js에서 실행 비활성화 필수 (§30)',
      '/Launch, /URI, /GoToR 액션 — 외부 실행·원격 접근 차단',
      '임베드 첨부파일이 실행 파일일 수 있음',
      '원격 이미지·폰트 참조로 인한 트래킹',
    ],
    notes: '스캔 PDF는 텍스트 레이어가 없어 검색 불가 — §14에 따라 사용자에게 명시. 안전한 redaction(§14)은 검게 덮는 방식이 아니라 콘텐츠 스트림에서 대상 객체를 제거하고 재작성한다.',
  },
  {
    id: 'docx', label: 'Word 문서 (DOCX)', extensions: ['.docx'],
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    family: 'document', spec: 'ECMA-376 / ISO-IEC 29500 (OOXML)', phase: 2,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: [
      '차트는 이미지 폴백으로 표시되며 편집 불가 (원본 파트는 보존)',
      '변경 내용 추적(tracked changes)은 표시만, 편집 시 병합되지 않음',
      '페이지 나눔 위치는 렌더러 차이로 원본과 다를 수 있음 (§16 경고 필요)',
    ],
    libraries: [LIB.mammoth, LIB.jszip, LIB.fxp, LIB.docx],
    securityRisks: OOXML_RISKS,
    notes: `읽기는 mammoth로 시맨틱 HTML을 얻고, 저장은 원본 ZIP의 word/document.xml만 교체하는 파트 보존 방식. ${PART_PRESERVING} 암호 보호 DOCX는 감지 후 안내만 한다 (§34).`,
  },
  {
    id: 'doc', label: 'Word 97-2003 문서 (DOC)', extensions: ['.doc'], mimeTypes: ['application/msword'],
    family: 'document', spec: 'MS-DOC (OLE2 복합 문서)', phase: 3,
    view: 'text-view', edit: 'convert-edit', platforms: uniform('text-view', 'convert-edit'),
    processing: 'local', fidelity: fid('low', 'low', 'medium', 'low', 'none', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '50 MB',
    lossOnSave: ['DOCX 변환 기반이므로 레이아웃·필드·매크로 손실 가능 — 저장 전 §16 경고 필수'],
    libraries: [LIB.docx],
    securityRisks: ['OLE2 스트림에 VBA 매크로·임베드 실행 파일 포함 가능', '구형 파서 취약점 표적이 되어온 포맷'],
    notes: '바이너리 OLE2 포맷이라 순정 JS 파서 품질이 낮다. 텍스트 추출 뷰 + DOCX 변환 후 편집 경로로 제공하며, 직접 편집은 제공하지 않는다 (§16).',
  },
  {
    id: 'hwpx', label: '한글 문서 (HWPX)', extensions: ['.hwpx'], mimeTypes: ['application/hwp+zip', 'application/vnd.hancom.hwpx'],
    family: 'document', spec: 'KS X 6101 (OWPML), 개방형 표준', phase: 2,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: ['수식(EQ) 객체는 보존만 되고 편집 불가', '차트·SmartArt류 객체는 이미지 폴백'],
    libraries: [LIB.jszip, LIB.fxp],
    securityRisks: OOXML_RISKS,
    notes: `HWPX는 ZIP+XML(OWPML) 개방 표준이라 DOCX와 동일한 파트 보존 파이프라인을 그대로 쓸 수 있다. ${PART_PRESERVING} 한글 폰트 미보유 시 폴백 전략은 §25를 따른다.`,
  },
  {
    id: 'hwp', label: '한글 문서 (HWP 5.0)', extensions: ['.hwp'], mimeTypes: ['application/x-hwp', 'application/haansofthwp'],
    family: 'document', spec: 'HWP 5.0 바이너리 (한글과컴퓨터 공개 문서)', phase: 3,
    view: 'needs-validation', edit: 'needs-validation',
    platforms: uniform('needs-validation', 'needs-validation'),
    processing: 'local', fidelity: fid('low', 'low', 'medium', 'medium', 'none', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '50 MB',
    lossOnSave: ['검증 완료 시 HWPX/DOCX/PDF 변환 경로만 제공. 원본 HWP로의 재저장은 어떤 경우에도 제공하지 않는다'],
    libraries: [LIB.jszip],
    securityRisks: [
      'OLE2 복합 문서 구조 — 스크립트·매크로 포함 가능',
      'HWP는 국내 표적 공격에서 악성 문서로 빈번히 사용된 이력이 있어 파서 격리가 특히 중요 (§30)',
    ],
    notes: 'HWP 5.0은 OLE2 복합 문서 + 레코드 스트림(zlib 압축) 구조. BodyText 스트림에서 본문 텍스트를 추출하는 경로는 실현 가능해 보이나 아직 실파일로 검증하지 않았고, 레이아웃 재현은 더욱 불확실하다. §19에 따라 검증 전까지는 뷰잉·편집 모두 needs-validation으로 둔다 — 계획을 지원으로 표기하지 않는다. 검증 후 목표는 §18의 텍스트 중심 뷰 + HWPX/DOCX/PDF 변환 편집이다. 한컴 SDK 라이선스 조사는 docs/03-license-inventory.md에서 별도로 다룬다.',
  },
  {
    id: 'rtf', label: 'RTF', extensions: ['.rtf'], mimeTypes: ['application/rtf', 'text/rtf'],
    family: 'document', spec: 'RTF 1.9.1 Specification', phase: 2,
    view: 'text-view', edit: 'convert-edit', platforms: uniform('text-view', 'convert-edit'),
    processing: 'local', fidelity: fid('low', 'low', 'medium', 'low', 'none', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '50 MB',
    lossOnSave: ['DOCX 변환 후 편집 — 원본 RTF 제어어 손실'],
    libraries: [], securityRisks: ['\\objdata OLE 임베드로 실행 파일 은닉 가능 — 임베드 객체 무시'],
    notes: '자체 파서를 작성한다(외부 의존 없음). 서식 있는 텍스트 위주 렌더링.',
  },
  {
    id: 'txt', label: '일반 텍스트', extensions: ['.txt', '.text', '.log', '.nfo'], mimeTypes: ['text/plain'],
    family: 'document', spec: 'Unicode / 인코딩별 표준', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '가상 스크롤로 1 GB까지 열람, 편집은 100 MB',
    lossOnSave: ['저장은 UTF-8로 정규화된다 (§22). 원본 인코딩 유지가 필요하면 사용자에게 선택지를 제공'],
    libraries: [], securityRisks: ['거대 단일 행으로 인한 렌더러 정지 — 행 길이 상한 적용'],
    notes: 'UTF-8/UTF-16/EUC-KR/Shift_JIS/GB18030 자동 판별 (§22, §25). BOM 처리 포함.',
  },
  {
    id: 'markdown', label: 'Markdown', extensions: ['.md', '.markdown', '.mdown', '.mkd'], mimeTypes: ['text/markdown'],
    family: 'document', spec: 'CommonMark 0.31 + GFM 확장', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('high', 'high', 'high', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '50 MB',
    lossOnSave: [],
    libraries: [LIB.marked, LIB.dompurify],
    securityRisks: ['원시 HTML 삽입 가능 — 렌더 전 DOMPurify 살균 및 스크립트 실행 차단 (§22, §30)'],
    notes: '원본 텍스트를 그대로 보존하는 무손실 편집. 렌더 뷰 ↔ 원본 뷰 전환 제공.',
  },
  {
    id: 'html', label: 'HTML', extensions: ['.html', '.htm', '.xhtml'], mimeTypes: ['text/html', 'application/xhtml+xml'],
    family: 'document', spec: 'WHATWG HTML Living Standard', phase: 2,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '50 MB',
    lossOnSave: [],
    libraries: [LIB.dompurify],
    securityRisks: [
      '스크립트 실행 — §22에 따라 자동 실행 절대 금지',
      '원격 리소스 로드로 인한 트래킹 — 샌드박스 iframe + CSP로 네트워크 차단',
    ],
    notes: '렌더 뷰는 스크립트·원격 요청을 차단한 샌드박스에서만 표시. 소스 뷰도 함께 제공한다.',
  },
  {
    id: 'epub', label: 'EPUB', extensions: ['.epub'], mimeTypes: ['application/epub+zip'],
    family: 'document', spec: 'EPUB 3.3 (W3C)', phase: 2,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('high', 'medium', 'high', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'low', softSizeLimit: '200 MB',
    lossOnSave: [],
    libraries: [LIB.epubjs, LIB.jszip, LIB.dompurify],
    securityRisks: ['내부 XHTML 스크립트 — 차단', 'ZIP 기반 위험 (§21)'],
    notes: 'DRM(Adobe ADEPT 등) 적용 파일은 감지 후 열 수 없음을 안내한다. DRM 우회는 하지 않는다.',
  },
  {
    id: 'odt', label: 'OpenDocument 텍스트', extensions: ['.odt'], mimeTypes: ['application/vnd.oasis.opendocument.text'],
    family: 'document', spec: 'ISO/IEC 26300 (ODF 1.3)', phase: 3,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'low', softSizeLimit: '100 MB',
    lossOnSave: ['차트·수식 객체는 보존만'],
    libraries: [LIB.jszip, LIB.fxp], securityRisks: OOXML_RISKS,
    notes: `ODF도 ZIP+XML이라 동일 파이프라인 재사용. ${PART_PRESERVING}`,
  },
  {
    id: 'pages', label: 'Apple Pages', extensions: ['.pages'], mimeTypes: ['application/vnd.apple.pages'],
    family: 'document', spec: '비공개 (IWA / Snappy + Protobuf)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: perPlatform({
      ios: ['standard-view', 'read-only'], macos: ['standard-view', 'read-only'],
    }, ['needs-validation', 'unsupported']),
    processing: 'local-native-assist', fidelity: fid('low', 'low', 'low', 'medium', 'none', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'low', softSizeLimit: '100 MB',
    lossOnSave: ['쓰기 미지원'],
    libraries: [LIB.jszip],
    securityRisks: ['ZIP 기반 위험 (§21)'],
    notes: '비공개 IWA 포맷. 번들 내 QuickLook 프리뷰(JPEG/PDF)가 있으면 그것을 표시하는 것이 현실적 경로다. iOS/macOS에서는 QuickLook에 위임한다. 완전 지원으로 표기하지 않는다 (§19).',
  },
];

/* ------------------------------------------------------------------ *
 * §7.2 Spreadsheets and data
 * ------------------------------------------------------------------ */

const spreadsheets: FormatSpec[] = [
  {
    id: 'xlsx', label: 'Excel 통합 문서 (XLSX)', extensions: ['.xlsx'],
    mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    family: 'spreadsheet', spec: 'ECMA-376 / ISO-IEC 29500 (OOXML)', phase: 1,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'medium'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'high', softSizeLimit: '모바일 25 MB / 데스크톱 200 MB, 행 가상화 적용',
    lossOnSave: [
      '피벗 테이블·슬라이서는 보존만 되고 편집 시 재계산되지 않음',
      '차트는 보존만 (편집 불가)',
      '조건부 서식 중 일부 고급 규칙은 표시만',
    ],
    libraries: [LIB.exceljs, LIB.sheetjs, LIB.jszip, LIB.fxp],
    securityRisks: [
      ...OOXML_RISKS,
      '외부 워크북 참조·웹 쿼리 — 네트워크 접근 전면 차단 (§15, §30)',
      'DDE/수식 인젝션 — 셀 값을 실행 가능한 형태로 해석하지 않음',
    ],
    notes: `수식은 원본 문자열과 캐시된 결과를 함께 보존한다. 재계산 불가 수식은 §15에 따라 삭제하지 않고 사용자에게 "이 수식은 현재 재계산할 수 없습니다"로 안내한다. ${PART_PRESERVING}`,
  },
  {
    id: 'xlsm', label: 'Excel 매크로 사용 통합 문서 (XLSM)', extensions: ['.xlsm'],
    mimeTypes: ['application/vnd.ms-excel.sheet.macroEnabled.12'],
    family: 'spreadsheet', spec: 'ECMA-376 (OOXML) + vbaProject.bin', phase: 2,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'medium'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'high', softSizeLimit: 'XLSX와 동일',
    lossOnSave: ['XLSX와 동일. 매크로는 보존되나 실행되지 않음'],
    libraries: [LIB.exceljs, LIB.jszip, LIB.fxp],
    securityRisks: ['VBA 매크로 포함이 확정적인 포맷 — 실행 절대 금지, 파일 정보 화면에 차단 사실 표시 (§30, §31)'],
    notes: '§15 지시대로 매크로를 삭제하지 않고 vbaProject.bin을 그대로 보존한 채 재저장한다. 실행은 어떤 경우에도 하지 않는다.',
  },
  {
    id: 'xls', label: 'Excel 97-2003 (XLS)', extensions: ['.xls'], mimeTypes: ['application/vnd.ms-excel'],
    family: 'spreadsheet', spec: 'MS-XLS (BIFF8, OLE2)', phase: 3,
    view: 'standard-view', edit: 'convert-edit', platforms: uniform('standard-view', 'convert-edit'),
    processing: 'local', fidelity: fid('low', 'low', 'high', 'low', 'none', 'medium'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '50 MB',
    lossOnSave: ['XLSX 변환 후 편집 — 원본 BIFF 고유 기능 손실 가능, 저장 전 경고'],
    libraries: [LIB.sheetjs],
    securityRisks: ['OLE2 매크로·임베드 객체', 'BIFF 파서 취약점 이력'],
    notes: 'SheetJS의 BIFF8 읽기 품질이 양호하다. 읽기는 직접, 편집은 XLSX 변환 경로.',
  },
  {
    id: 'csv', label: 'CSV', extensions: ['.csv'], mimeTypes: ['text/csv'],
    family: 'spreadsheet', spec: 'RFC 4180', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'high', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '스트리밍 + 가상 스크롤로 2 GB',
    lossOnSave: [],
    libraries: [LIB.papaparse],
    securityRisks: ['수식 인젝션(=, +, -, @로 시작하는 셀) — 표시만 하고 절대 평가하지 않음. 내보내기 시 이스케이프 옵션 제공'],
    notes: '구분자·인코딩·줄바꿈 자동 판별. 원본 인용 스타일을 보존하여 무손실 왕복 저장.',
  },
  {
    id: 'tsv', label: 'TSV', extensions: ['.tsv', '.tab'], mimeTypes: ['text/tab-separated-values'],
    family: 'spreadsheet', spec: 'IANA text/tab-separated-values', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'high', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: 'CSV와 동일',
    lossOnSave: [], libraries: [LIB.papaparse],
    securityRisks: ['CSV와 동일'], notes: 'CSV 핸들러를 구분자만 바꿔 재사용.',
  },
  {
    id: 'ods', label: 'OpenDocument 스프레드시트', extensions: ['.ods'],
    mimeTypes: ['application/vnd.oasis.opendocument.spreadsheet'],
    family: 'spreadsheet', spec: 'ISO/IEC 26300 (ODF 1.3)', phase: 3,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'medium'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: ['차트 보존만'], libraries: [LIB.sheetjs, LIB.jszip, LIB.fxp],
    securityRisks: OOXML_RISKS, notes: 'ODF 공통 파이프라인.',
  },
  {
    id: 'numbers', label: 'Apple Numbers', extensions: ['.numbers'], mimeTypes: ['application/vnd.apple.numbers'],
    family: 'spreadsheet', spec: '비공개 (IWA)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: perPlatform({
      ios: ['standard-view', 'read-only'], macos: ['standard-view', 'read-only'],
    }, ['needs-validation', 'unsupported']),
    processing: 'local-native-assist', fidelity: fid('low', 'low', 'low', 'low', 'none', 'none'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'low', softSizeLimit: '100 MB',
    lossOnSave: ['쓰기 미지원'], libraries: [LIB.jszip],
    securityRisks: ['ZIP 기반 위험'], notes: 'Pages와 동일하게 번들 내 프리뷰 이미지 또는 QuickLook 위임.',
  },
  {
    id: 'json', label: 'JSON', extensions: ['.json', '.jsonl', '.ndjson', '.json5'], mimeTypes: ['application/json'],
    family: 'data', spec: 'RFC 8259 / JSON Lines', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '트리 뷰 200 MB, 그 이상은 텍스트 모드',
    lossOnSave: ['정렬(pretty) 저장을 선택하면 원본 공백·키 순서가 바뀔 수 있음 — 기본값은 원본 보존'],
    libraries: [], securityRisks: ['깊은 중첩으로 인한 스택 오버플로 — 깊이 상한 적용', '프로토타입 오염 — 안전한 파서 사용'],
    notes: '§22 요구대로 검증 기능 제공. 트리 뷰 + 원본 뷰 + 오류 위치 표시.',
  },
  {
    id: 'xml', label: 'XML', extensions: ['.xml', '.xsd', '.xsl', '.xslt', '.rss', '.atom', '.plist'],
    mimeTypes: ['application/xml', 'text/xml'],
    family: 'data', spec: 'W3C XML 1.0 5th Edition', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: [], libraries: [LIB.fxp],
    securityRisks: [
      'XXE(외부 엔티티 확장) — 파서에서 엔티티 해석 비활성화 필수 (§30)',
      'Billion laughs 엔티티 폭탄 — 확장 상한',
      'XSLT 실행 금지',
    ],
    notes: '§22 검증 기능 제공. 트리 뷰 + 원본 뷰.',
  },
  {
    id: 'yaml', label: 'YAML', extensions: ['.yaml', '.yml'], mimeTypes: ['application/yaml', 'text/yaml'],
    family: 'data', spec: 'YAML 1.2.2', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '50 MB',
    lossOnSave: ['주석·앵커 보존을 위해 기본은 원본 텍스트 편집. 구조 편집 모드는 주석을 잃을 수 있어 경고한다'],
    libraries: [],
    securityRisks: [
      '커스텀 태그(!!python/object 등)로 인한 임의 객체 생성 — 안전 스키마만 허용 (§30)',
      '앵커/별칭 폭탄 — 확장 상한',
    ],
    notes: '§22 검증 기능 제공. 민감 정보를 담는 경우가 많아 §22의 기밀 파일 취급 규칙을 적용한다.',
  },
  {
    id: 'sqlite', label: 'SQLite 데이터베이스', extensions: ['.sqlite', '.sqlite3', '.db', '.db3'],
    mimeTypes: ['application/vnd.sqlite3', 'application/x-sqlite3'],
    family: 'data', spec: 'SQLite File Format 3', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'high', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'high', softSizeLimit: '메모리 로드 방식이라 100 MB',
    lossOnSave: ['읽기 전용'],
    libraries: [LIB.sqljs],
    securityRisks: [
      '임의 SQL 실행은 제공하지 않음 — 사전 정의된 조회만 허용',
      '개인정보를 담을 확률이 매우 높음 — 로그·썸네일·분석에 내용 노출 금지 (§22, §29)',
    ],
    notes: '테이블 목록·스키마·행 브라우징만 제공. 쓰기는 데이터 파괴 위험이 커 의도적으로 제외한다.',
  },
  {
    id: 'sql', label: 'SQL 스크립트', extensions: ['.sql'], mimeTypes: ['application/sql'],
    family: 'code', spec: 'ISO/IEC 9075 (방언 다수)', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: [], libraries: [LIB.prism],
    securityRisks: ['절대 실행하지 않는다 — 텍스트로만 취급', '접속 정보를 포함할 수 있어 기밀 파일로 취급 (§22)'],
    notes: '소스코드 핸들러로 처리. 실행 기능은 제공하지 않는다.',
  },
];

/* ------------------------------------------------------------------ *
 * §7.3 Presentations
 * ------------------------------------------------------------------ */

const presentations: FormatSpec[] = [
  {
    id: 'pptx', label: 'PowerPoint 프레젠테이션 (PPTX)', extensions: ['.pptx', '.ppsx'],
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    ],
    family: 'presentation', spec: 'ECMA-376 / ISO-IEC 29500 (OOXML)', phase: 2,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'high', 'high', 'low', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '200 MB (임베드 미디어가 대부분의 용량)',
    lossOnSave: [
      '애니메이션·전환 효과는 보존만 되고 재생·편집 불가',
      'SmartArt·차트는 이미지 폴백으로 표시 (원본 파트 보존)',
      '임베드 폰트는 보존하나 렌더링에는 사용하지 않을 수 있음',
    ],
    libraries: [LIB.jszip, LIB.fxp, LIB.pptxgen],
    securityRisks: [...OOXML_RISKS, '임베드 비디오·원격 미디어 자동 재생 차단'],
    notes: `슬라이드 XML을 직접 파싱해 도형·텍스트박스·이미지를 배치 렌더링한다. ${PART_PRESERVING} §17에 따라 애니메이션 등 미보존 기능은 저장 전에 명시적으로 고지한다.`,
  },
  {
    id: 'ppt', label: 'PowerPoint 97-2003 (PPT)', extensions: ['.ppt', '.pps'],
    mimeTypes: ['application/vnd.ms-powerpoint'],
    family: 'presentation', spec: 'MS-PPT (OLE2)', phase: 3,
    view: 'text-view', edit: 'convert-edit', platforms: uniform('text-view', 'convert-edit'),
    processing: 'local', fidelity: fid('low', 'low', 'low', 'low', 'none', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: ['PPTX 변환 후 편집 — 레이아웃 손실 가능'],
    libraries: [], securityRisks: ['OLE2 매크로·임베드 객체'],
    notes: '바이너리 OLE2. 텍스트·슬라이드 구조 추출 위주.',
  },
  {
    id: 'odp', label: 'OpenDocument 프레젠테이션', extensions: ['.odp'],
    mimeTypes: ['application/vnd.oasis.opendocument.presentation'],
    family: 'presentation', spec: 'ISO/IEC 26300 (ODF 1.3)', phase: 3,
    view: 'standard-view', edit: 'basic-edit', platforms: uniform('standard-view', 'basic-edit'),
    processing: 'local', fidelity: fid('medium', 'medium', 'medium', 'high', 'low', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '200 MB',
    lossOnSave: ['애니메이션 보존만'], libraries: [LIB.jszip, LIB.fxp],
    securityRisks: OOXML_RISKS, notes: 'ODF 공통 파이프라인.',
  },
  {
    id: 'keynote', label: 'Apple Keynote', extensions: ['.key'], mimeTypes: ['application/vnd.apple.keynote'],
    family: 'presentation', spec: '비공개 (IWA)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: perPlatform({
      ios: ['standard-view', 'read-only'], macos: ['standard-view', 'read-only'],
    }, ['needs-validation', 'unsupported']),
    processing: 'local-native-assist', fidelity: fid('low', 'low', 'low', 'medium', 'none', 'n/a'),
    passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
    largeFileRisk: 'medium', softSizeLimit: '200 MB',
    lossOnSave: ['쓰기 미지원'], libraries: [LIB.jszip],
    securityRisks: ['ZIP 기반 위험'], notes: 'Pages/Numbers와 동일 전략.',
  },
];

/* ------------------------------------------------------------------ *
 * §7.4 Images
 * ------------------------------------------------------------------ */

const IMG_EDIT_LOSS = ['재인코딩 시 화질 손실 (JPEG). 원본은 항상 보존되고 편집본은 새 파일로 저장된다 (§19)'];

const images: FormatSpec[] = [
  {
    id: 'jpeg', label: 'JPEG', extensions: ['.jpg', '.jpeg', '.jpe', '.jfif'], mimeTypes: ['image/jpeg'],
    family: 'image', spec: 'ISO/IEC 10918 / JFIF 1.02', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '해상도 기준 100 MP 이상은 다운샘플 프리뷰',
    lossOnSave: [...IMG_EDIT_LOSS, 'EXIF는 기본 보존하되, 공유 시 위치정보 제거 옵션을 제공 (§29)'],
    libraries: [LIB.browser],
    securityRisks: ['거대 해상도로 인한 메모리 고갈 — 디코드 전 픽셀 수 상한 검사 (§33)', 'EXIF에 위치·기기 정보 포함 (§29)'],
    notes: 'EXIF 방향 자동 보정. 디코딩은 브라우저 네이티브.',
  },
  {
    id: 'png', label: 'PNG', extensions: ['.png', '.apng'], mimeTypes: ['image/png', 'image/apng'],
    family: 'image', spec: 'ISO/IEC 15948 / W3C PNG 3rd Edition', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: 'JPEG와 동일',
    lossOnSave: ['무손실 재인코딩. 부가 청크(tEXt 등)는 손실될 수 있음'],
    libraries: [LIB.browser],
    securityRisks: ['압축 폭탄(작은 파일 → 거대 비트맵) — 픽셀 수 상한 검사'],
    notes: '알파 채널 보존. 투명 배경은 체커보드로 표시.',
  },
  {
    id: 'webp', label: 'WebP', extensions: ['.webp'], mimeTypes: ['image/webp'],
    family: 'image', spec: 'RFC 9649 (WebP)', phase: 1,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: 'JPEG와 동일',
    lossOnSave: IMG_EDIT_LOSS, libraries: [LIB.browser],
    securityRisks: ['압축 폭탄'], notes: '애니메이션 WebP는 재생 지원, 편집은 첫 프레임 기준.',
  },
  {
    id: 'gif', label: 'GIF', extensions: ['.gif'], mimeTypes: ['image/gif'],
    family: 'image', spec: 'GIF89a', phase: 1,
    view: 'full-view', edit: 'basic-edit', platforms: uniform('full-view', 'basic-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '프레임 수 상한 적용',
    lossOnSave: ['편집 결과는 PNG/WebP로 내보낸다. GIF 재인코딩은 제공하지 않아 애니메이션이 정지 이미지가 됨 — 저장 전 고지'],
    libraries: [LIB.browser],
    securityRisks: ['과다 프레임 GIF로 인한 메모리 고갈'],
    notes: '애니메이션 재생 지원(§19). 편집은 정지 프레임 대상.',
  },
  {
    id: 'heic', label: 'HEIC / HEIF', extensions: ['.heic', '.heif', '.hif'], mimeTypes: ['image/heic', 'image/heif'],
    family: 'image', spec: 'ISO/IEC 23008-12 (HEIF)', phase: 2,
    view: 'full-view', edit: 'convert-edit',
    platforms: perPlatform({
      ios: ['full-view', 'convert-edit'], macos: ['full-view', 'convert-edit'],
      android: ['full-view', 'convert-edit'], windows: ['standard-view', 'convert-edit'],
      web: ['needs-validation', 'unsupported'],
    }, ['standard-view', 'convert-edit']),
    processing: 'local-native-assist', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '50 MB',
    lossOnSave: ['편집·내보내기는 JPEG/PNG/WebP로만. HEIC 재인코딩은 특허 이슈로 제공하지 않는다 (§20)'],
    libraries: [LIB.osImage],
    securityRisks: ['HEIF 파서 취약점 이력 다수 — OS 디코더 위임이 보안상으로도 유리'],
    notes: 'ADR-001 결정에 따라 libheif-js(LGPL-3.0)는 채택하지 않는다. 모바일·데스크톱은 OS 디코더, 웹은 브라우저 네이티브 지원(Safari) 여부를 감지해 미지원 시 §19대로 안내한다.',
  },
  {
    id: 'avif', label: 'AVIF', extensions: ['.avif'], mimeTypes: ['image/avif'],
    family: 'image', spec: 'AV1 Image File Format (AOM)', phase: 2,
    view: 'full-view', edit: 'convert-edit',
    platforms: perPlatform({ windows: ['standard-view', 'convert-edit'] }, ['full-view', 'convert-edit']),
    processing: 'local-native-assist', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '50 MB',
    lossOnSave: ['내보내기는 JPEG/PNG/WebP'], libraries: [LIB.browser, LIB.osImage],
    securityRisks: ['디코더 취약점'], notes: '주요 브라우저가 네이티브 지원한다. 인코딩은 제공하지 않는다.',
  },
  {
    id: 'bmp', label: 'BMP', extensions: ['.bmp', '.dib'], mimeTypes: ['image/bmp'],
    family: 'image', spec: 'Windows Bitmap (BITMAPINFOHEADER 계열)', phase: 2,
    view: 'full-view', edit: 'convert-edit', platforms: uniform('full-view', 'convert-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '무압축이라 용량이 크다 — 100 MB',
    lossOnSave: ['내보내기는 PNG 권장'], libraries: [LIB.browser],
    securityRisks: ['헤더 조작을 통한 과대 할당 유도'], notes: '무압축 포맷 특성상 메모리 사용량 검사 필수.',
  },
  {
    id: 'tiff', label: 'TIFF', extensions: ['.tif', '.tiff'], mimeTypes: ['image/tiff'],
    family: 'image', spec: 'TIFF 6.0 / Adobe', phase: 2,
    view: 'full-view', edit: 'convert-edit',
    platforms: perPlatform({
      macos: ['full-view', 'convert-edit'], ios: ['full-view', 'convert-edit'],
      windows: ['standard-view', 'convert-edit'],
    }, ['needs-validation', 'unsupported']),
    processing: 'local-native-assist', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '멀티페이지·대형 스캔이 흔하다 — 200 MB',
    lossOnSave: ['내보내기는 PNG/PDF'], libraries: [LIB.osImage],
    securityRisks: ['압축 방식이 다양해 파서 표면이 넓다'],
    notes: '브라우저는 TIFF를 네이티브 지원하지 않는다. 웹에서는 미지원으로 정직하게 표시한다 (§19). 멀티페이지 TIFF는 페이지 탐색 UI를 제공한다.',
  },
  {
    id: 'svg', label: 'SVG', extensions: ['.svg', '.svgz'], mimeTypes: ['image/svg+xml'],
    family: 'image', spec: 'W3C SVG 1.1 / SVG 2', phase: 2,
    view: 'full-view', edit: 'basic-edit', platforms: uniform('full-view', 'basic-edit'),
    processing: 'local', fidelity: fid('high', 'medium', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '20 MB',
    lossOnSave: [],
    libraries: [LIB.dompurify, LIB.fxp],
    securityRisks: [
      '<script>·이벤트 핸들러·<foreignObject>로 임의 스크립트 실행 — §22에 따라 자동 실행 금지, DOMPurify로 살균 후 렌더',
      '외부 참조(xlink:href, use)로 인한 원격 요청 — 차단',
      '재귀 <use>로 인한 렌더러 정지',
    ],
    notes: '살균 전후를 사용자에게 알린다. 소스 편집 모드 제공.',
  },
  {
    id: 'ico', label: 'ICO', extensions: ['.ico', '.cur'], mimeTypes: ['image/x-icon', 'image/vnd.microsoft.icon'],
    family: 'image', spec: 'Windows Icon Resource', phase: 2,
    view: 'full-view', edit: 'convert-edit', platforms: uniform('full-view', 'convert-edit'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '10 MB',
    lossOnSave: ['내보내기는 PNG'], libraries: [LIB.browser],
    securityRisks: [], notes: '다중 해상도 이미지를 모두 나열해 보여준다.',
  },
  {
    id: 'raw', label: 'RAW 이미지', extensions: [
      '.dng', '.cr2', '.cr3', '.nef', '.nrw', '.arw', '.srf', '.sr2',
      '.orf', '.rw2', '.raf', '.pef', '.raw', '.rwl', '.x3f',
    ], mimeTypes: ['image/x-adobe-dng', 'image/x-canon-cr2', 'image/x-nikon-nef', 'image/x-sony-arw'],
    family: 'image', spec: '제조사별 비공개 (DNG는 Adobe 공개 규격)', phase: 3,
    view: 'standard-view', edit: 'unsupported',
    platforms: perPlatform({
      ios: ['standard-view', 'unsupported'], macos: ['standard-view', 'unsupported'],
    }, ['needs-validation', 'unsupported']),
    processing: 'local-native-assist', fidelity: fid('medium', 'n/a', 'n/a', 'medium', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '파일당 20~100 MB가 일반적',
    lossOnSave: ['쓰기 미지원'], libraries: [LIB.osImage],
    securityRisks: ['제조사별 파서 표면이 넓고 검증이 어렵다'],
    notes: '현실적 1차 경로는 RAW 내부에 임베드된 JPEG 프리뷰를 추출해 표시하는 것이다(대부분의 RAW가 포함). 완전 현상(demosaic)은 제공하지 않으며, 프리뷰임을 사용자에게 명시한다 (§19, §31).',
  },
];

/* ------------------------------------------------------------------ *
 * §7.5 Video / §7.6 Audio
 * ------------------------------------------------------------------ */

const CODEC_NOTE = '재생은 OS/브라우저 코덱에 위임한다. §20에 따라 특허 코덱 인코더는 번들하지 않으며, 코덱 미지원 시 §19대로 정직하게 안내한다.';
const MEDIA_RISKS = ['컨테이너 파서 취약점', '자동 재생으로 인한 원격 리소스 요청 — 로컬 파일만 재생하고 외부 URL은 차단'];

const mkMedia = (
  id: string, label: string, exts: string[], mimes: string[],
  family: 'video' | 'audio', spec: string, phase: 1 | 2 | 3,
  headline: readonly [view: SupportLevel, edit: SupportLevel],
  platforms: PlatformSupport, notes: string,
): FormatSpec => ({
  id, label, extensions: exts, mimeTypes: mimes, family, spec, phase,
  view: headline[0], edit: headline[1], platforms,
  processing: 'local-native-assist',
  fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
  passwordSupport: 'n/a', encryptionSupport: 'n/a',
  largeFileRisk: 'medium', softSizeLimit: '스트리밍 재생이므로 용량 제한 없음. 편집은 2 GB',
  lossOnSave: ['트리밍은 재인코딩 없는 스트림 복사(무손실)만 제공한다. 재인코딩이 필요한 편집은 제공하지 않는다 (§20)'],
  libraries: [LIB.osMedia, LIB.browser], securityRisks: MEDIA_RISKS,
  notes: `${notes} ${CODEC_NOTE}`,
});

const video: FormatSpec[] = [
  // Video ships playback only — trimming a video losslessly is not implemented,
  // so §19 requires the edit level to say read-only rather than advertise an
  // edit that does not exist. Audio (WAV/MP3) keeps basic-edit because its trim
  // does ship.
  mkMedia('mp4', 'MP4', ['.mp4', '.m4v'], ['video/mp4'], 'video', 'ISO/IEC 14496-14', 2,
    ['standard-view', 'read-only'], uniform('standard-view', 'read-only'),
    'H.264/H.265 컨테이너. 전 플랫폼 재생 가능성이 가장 높다.'),
  mkMedia('mov', 'QuickTime MOV', ['.mov', '.qt'], ['video/quicktime'], 'video', 'QuickTime File Format', 2,
    ['standard-view', 'read-only'],
    perPlatform({ web: ['needs-validation', 'unsupported'] }, ['standard-view', 'read-only']),
    '내부 코덱에 따라 브라우저 재생 여부가 갈린다.'),
  mkMedia('webm', 'WebM', ['.webm'], ['video/webm'], 'video', 'WebM Project (Matroska 서브셋)', 2,
    ['full-view', 'read-only'], uniform('full-view', 'read-only'),
    '로열티 프리 코덱(VP8/VP9/AV1). 웹에서 가장 안전한 선택.'),
  mkMedia('mkv', 'Matroska', ['.mkv'], ['video/x-matroska'], 'video', 'Matroska Specification', 2,
    ['standard-view', 'basic-edit'],
    perPlatform({ web: ['needs-validation', 'unsupported'] }, ['standard-view', 'basic-edit']),
    '브라우저 네이티브 지원이 없어 웹에서는 대체로 재생 불가.'),
  mkMedia('avi', 'AVI', ['.avi'], ['video/x-msvideo'], 'video', 'Microsoft RIFF AVI', 2,
    ['standard-view', 'basic-edit'],
    perPlatform({ web: ['unsupported', 'unsupported'] }, ['standard-view', 'basic-edit']),
    '구형 컨테이너. 웹 재생 불가.'),
  mkMedia('wmv', 'WMV', ['.wmv', '.asf'], ['video/x-ms-wmv'], 'video', 'Advanced Systems Format', 3,
    ['standard-view', 'read-only'],
    perPlatform({ windows: ['standard-view', 'read-only'] }, ['unsupported', 'unsupported']),
    'Windows 외 지원이 사실상 없다. DRM 적용 파일은 열지 않는다.'),
  // ".ts" and ".mts" are deliberately absent: both collide with TypeScript
  // source, which a file viewer sees far more often than a raw transport
  // stream. Real transport streams — including AVCHD .mts from camcorders —
  // are identified by their 0x47 sync-byte signature instead, so nothing is
  // lost. ".m2ts" is unambiguous and stays.
  mkMedia('mpeg', 'MPEG-1/2', ['.mpeg', '.mpg', '.m2v', '.m2ts'], ['video/mpeg'], 'video',
    'ISO/IEC 11172 / 13818', 3, ['standard-view', 'read-only'],
    perPlatform({ web: ['needs-validation', 'unsupported'] }, ['standard-view', 'read-only']),
    '구형 방송·DVD 계열.'),
];

const audio: FormatSpec[] = [
  mkMedia('mp3', 'MP3', ['.mp3'], ['audio/mpeg'], 'audio', 'ISO/IEC 11172-3', 2,
    ['full-view', 'basic-edit'], uniform('full-view', 'basic-edit'),
    'ID3 태그 표시. 파형 렌더링 제공.'),
  mkMedia('wav', 'WAV', ['.wav', '.wave'], ['audio/wav', 'audio/x-wav'], 'audio', 'Microsoft/IBM RIFF WAVE', 2,
    ['full-view', 'basic-edit'], uniform('full-view', 'basic-edit'),
    '무압축. 트리밍이 완전 무손실이다.'),
  mkMedia('m4a', 'M4A / AAC', ['.m4a', '.aac'], ['audio/mp4', 'audio/aac'], 'audio', 'ISO/IEC 14496-3', 2,
    ['full-view', 'basic-edit'], uniform('full-view', 'basic-edit'),
    'AAC 인코딩은 특허 대상이므로 제공하지 않는다.'),
  mkMedia('flac', 'FLAC', ['.flac'], ['audio/flac'], 'audio', 'RFC 9639', 2,
    ['full-view', 'basic-edit'], uniform('full-view', 'basic-edit'),
    '로열티 프리 무손실 코덱.'),
  mkMedia('ogg', 'Ogg Vorbis', ['.ogg', '.oga'], ['audio/ogg'], 'audio', 'RFC 3533 / Vorbis I', 2,
    ['full-view', 'basic-edit'],
    perPlatform({ ios: ['needs-validation', 'unsupported'] }, ['full-view', 'basic-edit']),
    '로열티 프리. iOS 네이티브 지원이 제한적이다.'),
  mkMedia('opus', 'Opus', ['.opus'], ['audio/opus'], 'audio', 'RFC 6716', 2,
    ['full-view', 'basic-edit'], uniform('full-view', 'basic-edit'), '로열티 프리.'),
  mkMedia('wma', 'WMA', ['.wma'], ['audio/x-ms-wma'], 'audio', 'Advanced Systems Format', 3,
    ['standard-view', 'read-only'],
    perPlatform({ windows: ['standard-view', 'read-only'] }, ['unsupported', 'unsupported']),
    'Windows 외 지원 없음.'),
];

/* ------------------------------------------------------------------ *
 * §7.7 Archives
 * ------------------------------------------------------------------ */

const ARCHIVE_RISKS = [
  'Zip Slip — 엔트리 경로의 ../ 탈출 (§21)',
  '압축 폭탄 — 압축비·전개 크기 상한 필수 (§21)',
  '재귀 아카이브 — 중첩 깊이 상한',
  '과다 엔트리 수 — 개수 상한',
  '심볼릭 링크 엔트리로 승인 경로 밖 쓰기 (§21)',
  '확장자 위장 실행 파일 — 전개 후 실행 절대 금지 (§21)',
];

const mkArchive = (
  id: string, label: string, exts: string[], mimes: string[], spec: string,
  phase: 2 | 3, view: SupportLevel, edit: SupportLevel, libs: FormatSpec['libraries'], notes: string,
): FormatSpec => ({
  id, label, extensions: exts, mimeTypes: mimes, family: 'archive', spec, phase,
  view, edit, platforms: uniform(view, edit), processing: 'local',
  fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
  passwordSupport: 'detected-not-handled', encryptionSupport: 'detected-not-handled',
  largeFileRisk: 'high', softSizeLimit: '목록 표시는 제한 없음, 전개는 여유 디스크의 50%까지',
  lossOnSave: [], libraries: libs, securityRisks: ARCHIVE_RISKS,
  notes: `${notes} §21에 따라 목록 표시를 위해 전체 전개를 하지 않는다 — 중앙 디렉터리만 읽는다.`,
});

const archives: FormatSpec[] = [
  mkArchive('zip', 'ZIP', ['.zip'], ['application/zip'], 'PKWARE APPNOTE 6.3.10', 2,
    'full-view', 'basic-edit', [LIB.fflate, LIB.jszip],
    '개별 파일 미리보기·선택 전개·새 아카이브 생성 지원. 암호 보호 ZIP은 감지 후 안내한다(§21).'),
  mkArchive('7z', '7-Zip', ['.7z'], ['application/x-7z-compressed'], '7-Zip Format Specification', 3,
    'needs-validation', 'read-only', [],
    'LZMA/LZMA2 전개에 WASM 포팅이 필요하다. 라이선스(LGPL/공용) 검토 후 결정한다.'),
  mkArchive('rar', 'RAR', ['.rar'], ['application/vnd.rar', 'application/x-rar-compressed'], 'RAR 5.0 (비공개)', 3,
    'needs-validation', 'read-only', [],
    'RARLAB의 unrar 소스는 "RAR 호환 압축기 제작 금지" 조항이 있어 배포 전 법률 검토가 필요하다. 압축은 어떤 경우에도 제공하지 않는다.'),
  mkArchive('tar', 'TAR', ['.tar'], ['application/x-tar'], 'POSIX.1-2001 (pax)', 2,
    'full-view', 'read-only', [],
    '자체 파서 작성(단순 구조). .tar.gz / .tar.bz2 / .tar.xz 조합도 처리한다.'),
  mkArchive('gzip', 'GZIP', ['.gz', '.tgz'], ['application/gzip'], 'RFC 1952', 2,
    'full-view', 'read-only', [LIB.fflate], '단일 스트림 압축. tar와 조합되는 경우가 대부분이다.'),
  mkArchive('bzip2', 'BZIP2', ['.bz2', '.tbz2'], ['application/x-bzip2'], 'bzip2 형식', 3,
    'needs-validation', 'read-only', [], 'WASM 디코더 필요.'),
  mkArchive('xz', 'XZ', ['.xz', '.txz'], ['application/x-xz'], 'XZ File Format 1.1.0', 3,
    'needs-validation', 'read-only', [], 'LZMA2 기반. 7z와 같은 디코더를 공유할 수 있다.'),
];

/* ------------------------------------------------------------------ *
 * §7.8 Design and professional formats
 * ------------------------------------------------------------------ */

const design: FormatSpec[] = [
  {
    id: 'psd', label: 'Photoshop 문서', extensions: ['.psd', '.psb'], mimeTypes: ['image/vnd.adobe.photoshop'],
    family: 'design', spec: 'Adobe Photoshop File Format Specification', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('medium', 'low', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '데스크톱 500 MB / 모바일 50 MB',
    lossOnSave: ['읽기 전용. 내보내기는 PNG/JPEG 합성 이미지'],
    libraries: [LIB.agpsd],
    securityRisks: ['거대 레이어 수·해상도로 인한 메모리 고갈'],
    notes: '레이어 트리 표시 + 레이어 표시/숨김 토글 + 합성 이미지 표시. 텍스트 레이어는 렌더된 결과로만 표시된다.',
  },
  {
    id: 'ai', label: 'Adobe Illustrator', extensions: ['.ai'], mimeTypes: ['application/postscript', 'application/illustrator'],
    family: 'design', spec: 'PDF 호환 (현대 .ai) / EPS (구형)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('medium', 'medium', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '200 MB',
    lossOnSave: ['읽기 전용'], libraries: [LIB.pdfjs],
    securityRisks: ['PostScript 인터프리터는 실행 위험이 있어 사용하지 않는다'],
    notes: '현대 .ai 파일은 PDF 호환 구조라 pdf.js로 표시 가능하다. 구형 EPS 기반 .ai는 미지원으로 표시한다 (§19).',
  },
  {
    id: 'eps', label: 'EPS', extensions: ['.eps'], mimeTypes: ['application/postscript'],
    family: 'design', spec: 'Encapsulated PostScript 3.0', phase: 3,
    view: 'needs-validation', edit: 'read-only', platforms: uniform('needs-validation', 'read-only'),
    processing: 'local', fidelity: fid('low', 'low', 'n/a', 'medium', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '100 MB',
    lossOnSave: ['읽기 전용'], libraries: [],
    securityRisks: ['PostScript는 튜링 완전한 실행 언어 — 인터프리터를 절대 실행하지 않는다 (§30)'],
    notes: '임베드된 TIFF/WMF 프리뷰가 있으면 그것만 표시한다. PostScript 실행은 하지 않으므로 프리뷰 없는 EPS는 미지원이다.',
  },
  {
    id: 'sketch', label: 'Sketch', extensions: ['.sketch'], mimeTypes: ['application/zip'],
    family: 'design', spec: 'Sketch File Format (ZIP + JSON)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('medium', 'low', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '200 MB',
    lossOnSave: ['읽기 전용'], libraries: [LIB.jszip],
    securityRisks: ['ZIP 기반 위험'],
    notes: 'ZIP 내부에 아트보드 프리뷰 PNG가 포함되어 있어 페이지·아트보드 목록과 함께 표시할 수 있다.',
  },
  {
    id: 'dxf', label: 'DXF', extensions: ['.dxf'], mimeTypes: ['image/vnd.dxf', 'application/dxf'],
    family: 'design', spec: 'Autodesk DXF Reference (공개)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('medium', 'low', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '엔티티 수 상한 적용',
    lossOnSave: ['읽기 전용'], libraries: [LIB.dxfparser],
    securityRisks: ['엔티티 수 폭증으로 인한 렌더러 정지'],
    notes: '2D 도면 벡터 렌더링 + 레이어 토글. 치수·해치 등 일부 엔티티는 단순화된다.',
  },
  {
    id: 'dwg', label: 'DWG', extensions: ['.dwg'], mimeTypes: ['image/vnd.dwg', 'application/acad'],
    family: 'design', spec: '비공개 (Autodesk)', phase: 3,
    view: 'commercial-license', edit: 'unsupported', platforms: uniform('commercial-license', 'unsupported'),
    processing: 'server-required', fidelity: fid('none', 'none', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '—',
    lossOnSave: ['미지원'], libraries: [],
    securityRisks: [],
    notes: '비공개 바이너리 포맷. 신뢰할 만한 오픈소스 파서가 없고 실질적 선택지는 ODA(Open Design Alliance) 상용 라이선스뿐이다(연회비 수천 달러 규모). §32 원가 모델과 충돌하므로 현 단계에서는 미지원으로 표기하고, DXF 변환 후 열도록 안내한다 (§19, §34).',
  },
  {
    id: 'gltf', label: 'glTF / GLB', extensions: ['.gltf', '.glb'], mimeTypes: ['model/gltf+json', 'model/gltf-binary'],
    family: 'design', spec: 'Khronos glTF 2.0', phase: 3,
    view: 'full-view', edit: 'read-only', platforms: uniform('full-view', 'read-only'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'high', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '데스크톱 500 MB / 모바일 50 MB',
    lossOnSave: ['읽기 전용'], libraries: [LIB.three],
    securityRisks: ['외부 URI 참조 텍스처 — 원격 요청 차단', '거대 메시로 인한 GPU 메모리 고갈'],
    notes: '3D 표준 포맷. §11에 따라 저해상도 프리뷰를 먼저 표시한 뒤 상세 로드.',
  },
  {
    id: 'obj', label: 'OBJ', extensions: ['.obj', '.mtl'], mimeTypes: ['model/obj'],
    family: 'design', spec: 'Wavefront OBJ (사실상 표준)', phase: 3,
    view: 'standard-view', edit: 'read-only', platforms: uniform('standard-view', 'read-only'),
    processing: 'local', fidelity: fid('medium', 'n/a', 'n/a', 'medium', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '200 MB',
    lossOnSave: ['읽기 전용'], libraries: [LIB.three],
    securityRisks: ['텍스트 포맷이라 파싱 비용이 크다 — 정점 수 상한'],
    notes: '.mtl 재질 파일이 함께 있어야 재질이 적용된다. 단독 열람 시 기본 재질로 표시하고 그 사실을 알린다.',
  },
  {
    id: 'stl', label: 'STL', extensions: ['.stl'], mimeTypes: ['model/stl'],
    family: 'design', spec: 'STL (ASCII / Binary)', phase: 3,
    view: 'full-view', edit: 'read-only', platforms: uniform('full-view', 'read-only'),
    processing: 'local', fidelity: fid('high', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '200 MB',
    lossOnSave: ['읽기 전용'], libraries: [LIB.three],
    securityRisks: ['삼각형 수 상한'], notes: '3D 프린팅 표준. 재질·색상 정보가 없다.',
  },
  {
    id: 'fbx', label: 'FBX', extensions: ['.fbx'], mimeTypes: ['application/octet-stream'],
    family: 'design', spec: '비공개 (Autodesk). 커뮤니티 역공학 문서 존재', phase: 3,
    view: 'needs-validation', edit: 'unsupported', platforms: uniform('needs-validation', 'unsupported'),
    processing: 'local', fidelity: fid('medium', 'n/a', 'n/a', 'medium', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'high', softSizeLimit: '200 MB',
    lossOnSave: ['미지원'], libraries: [LIB.three],
    securityRisks: [],
    notes: 'three.js의 FBXLoader가 있으나 버전별 호환성 편차가 크다. 검증 전까지 완전 지원으로 표기하지 않는다 (§19).',
  },
];

/* ------------------------------------------------------------------ *
 * §7.9 Source code and configuration
 * ------------------------------------------------------------------ */

const sourcecode: FormatSpec[] = [
  {
    id: 'sourcecode', label: '소스코드 · 설정 파일',
    extensions: [
      '.css', '.scss', '.sass', '.less',
      '.js', '.mjs', '.cjs', '.jsx', '.ts', '.tsx', '.mts', '.cts',
      '.py', '.pyi', '.java', '.kt', '.kts', '.swift', '.dart',
      '.php', '.go', '.rs', '.c', '.h', '.cpp', '.cc', '.cxx', '.hpp', '.cs',
      '.rb', '.pl', '.lua', '.r', '.scala', '.groovy', '.sh', '.bash', '.zsh',
      '.ps1', '.psm1', '.bat', '.cmd', '.vue', '.svelte', '.astro',
      '.toml', '.ini', '.conf', '.cfg', '.properties', '.editorconfig',
      '.gitignore', '.dockerignore', '.dockerfile', '.makefile', '.gradle',
      '.srt', '.vtt', '.diff', '.patch',
    ],
    mimeTypes: ['text/plain', 'text/javascript', 'text/css', 'application/x-sh'],
    family: 'code', spec: '언어별 상이', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'medium', softSizeLimit: '가상 스크롤로 500 MB',
    lossOnSave: ['저장은 UTF-8. 원본 줄바꿈(CRLF/LF)과 들여쓰기는 보존한다'],
    libraries: [LIB.prism],
    securityRisks: [
      '절대 실행하지 않는다 (§22)',
      '자격 증명을 포함할 수 있어 로그·분석·썸네일에 내용을 노출하지 않는다 (§22, §29)',
    ],
    notes: '단일 핸들러가 확장자로 문법 강조 언어만 전환한다. §22의 줄번호·검색·치환·코드 폴딩·인코딩 판별을 공통 제공.',
  },
  {
    id: 'dotenv', label: '환경 변수 파일', extensions: ['.env'], mimeTypes: ['text/plain'],
    family: 'code', spec: '사실상 표준 (KEY=VALUE)', phase: 2,
    view: 'full-view', edit: 'full-edit', platforms: uniform('full-view', 'full-edit'),
    processing: 'local', fidelity: fid('n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'),
    passwordSupport: 'n/a', encryptionSupport: 'n/a',
    largeFileRisk: 'low', softSizeLimit: '10 MB',
    lossOnSave: [], libraries: [],
    securityRisks: ['거의 항상 자격 증명을 담는다 — §22에 따라 기밀 파일로 분류'],
    notes: '기밀 파일로 표시하고 값은 기본적으로 마스킹한 뒤 사용자가 명시적으로 표시를 선택하게 한다. 최근 파일 목록·썸네일·오류 보고·원격 전송에서 제외한다 (§22, §29).',
  },
];

/* ------------------------------------------------------------------ *
 * exports
 * ------------------------------------------------------------------ */

export const FORMAT_CATALOG: readonly FormatSpec[] = Object.freeze([
  ...documents, ...spreadsheets, ...presentations, ...images,
  ...video, ...audio, ...archives, ...design, ...sourcecode,
]);

/** Format id → spec. */
export const FORMATS_BY_ID: ReadonlyMap<string, FormatSpec> =
  new Map(FORMAT_CATALOG.map((f) => [f.id, f]));

/** Lower-case dotted extension → spec. First registration wins. */
export const FORMATS_BY_EXTENSION: ReadonlyMap<string, FormatSpec> = (() => {
  const m = new Map<string, FormatSpec>();
  for (const f of FORMAT_CATALOG) {
    for (const ext of f.extensions) if (!m.has(ext)) m.set(ext, f);
  }
  return m;
})();

/** Lower-case MIME type → spec. First registration wins. */
export const FORMATS_BY_MIME: ReadonlyMap<string, FormatSpec> = (() => {
  const m = new Map<string, FormatSpec>();
  for (const f of FORMAT_CATALOG) {
    for (const mime of f.mimeTypes) {
      const key = mime.toLowerCase();
      if (!m.has(key)) m.set(key, f);
    }
  }
  return m;
})();
